// 🔑 THE GUIDED ACTIONS, IN THE UNDERWRITING MODULE (Jesse, 2026-10-24 strip-back: "Remove the per-step panels, the
// Letters panel, pricing, re-price, override, overturn and consents from the tab. Those actions stay available in the
// Underwriting Module"). The Module shows one part per step:
//   part 'step56'   (its Income step)  — the step-5 re-check, step-6 figures, disputes and the step-6 override
//   part 'letters'  (its Credit step)  — Dismiss a letter with a reason (Accept / ask again / factors are the Module's own)
//   part 'decision' (its Decision step) — the decision, notices, re-price, the review task (uphold / overturn), signatures
//   part 'consents' (its Consents step) — every consent with time, IP and user agent
// Same routes and reasons as before (utils/guidedRedecide via routes/admin.js). Joint files: a column per applicant.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API, B, stamp, day, money, nameOf, consentLabel, cleanVersion, reasonWords, ReasonAction, Row, Card, Label } from './parts';
import { formatRate } from '../../../constants/rateFormat';
import GuidedCheckHolds from './GuidedCheckHolds';

const PRICING_FIELDS = [['grossMonthlyIncome', 'Income per month'], ['totalMonthlyDebt', 'Debts per month'], ['availableFunds', 'Funds available'],
  ['fico', 'Credit score'], ['downPaymentPct', 'Down payment %'], ['targetPurchasePrice', 'Target price']];
function PricingInputs({ value, onChange, testid }) {
  return (
    <div data-testid={testid} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
      {PRICING_FIELDS.map(([k, label]) => (
        <label key={k} style={{ fontSize: 12, color: B.ink, minWidth: 0 }}>{label}
          <input data-testid={`${testid}-${k}`} placeholder="blank = the file's" value={value[k] || ''} onChange={(e) => onChange({ ...value, [k]: e.target.value })}
            style={{ display: 'block', width: '100%', marginTop: 3, fontSize: 13, padding: 7, borderRadius: 6, border: `1px solid ${B.slate}`, boxSizing: 'border-box' }} />
        </label>
      ))}
    </div>
  );
}

const Columns = ({ view, children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: view.applicants.length > 1 ? 'repeat(auto-fit, minmax(280px, 1fr))' : '1fr', gap: 16 }}>
    {view.applicants.map((a) => (
      <div key={a.userId} data-testid="guided-module-applicant" style={{ minWidth: 0, display: 'grid', gap: 8, alignContent: 'start' }}>
        {view.applicants.length > 1 && <div style={{ fontSize: 13, fontWeight: 700, color: B.ink }}>{a.role === 'primary' ? 'Primary' : 'Co-borrower'} · {nameOf(a)}</div>}
        {children(a)}
      </div>
    ))}
  </div>
);

export default function GuidedModuleActions({ part, borrowerId, token, onChanged, loxConditions = [] }) {
  const [view, setView] = useState(null);
  const [err, setErr] = useState(null);
  const [override, setOverride] = useState({});
  const [repriceIn, setRepriceIn] = useState({});
  const [overturn, setOverturn] = useState({ mode: 'computed', inputs: {} });
  const headers = { Authorization: `Bearer ${token}` };
  const api = { post: (p, b) => axios.post(`${API}/api/admin${p}`, b, { headers }), patch: (p, b) => axios.patch(`${API}/api/admin${p}`, b, { headers }) };
  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/admin/borrowers/${borrowerId}/guided-review`, { headers }); setView(r.data); setErr(null); }
    catch (_) { setErr('The file could not be loaded just now.'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [borrowerId, token]);
  useEffect(() => { load(); }, [load]);
  const changed = async () => { await load(); if (onChanged) await onChanged(); };

  if (err) return <div role="alert" style={{ color: B.red, fontSize: 13 }}>{err}</div>;
  if (!view) return <div style={{ color: B.slate, fontSize: 13 }}>Loading…</div>;
  if (!view.guided) return null;

  // 🔑 THE STEPS 5–6 CARD, SPLIT ONTO THE STEPS THAT OWN EACH PIECE (Jesse, 2026-10-26): the re-check (and a held check)
  // on Docs, a dispute on Income or Assets, debts on Credit. Savings show on Assets and the figure changes there.
  // 🔑 Beside "All documents" on every Module step (2026-10-26): the re-check inline (one per applicant), and a held check.
  if (part === 'recheck-inline') {
    return (
      <div data-testid="guided-module-recheck" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
        {view.applicants.map((a) => (
          <ReasonAction key={a.userId} label={view.applicants.length > 1 ? `Re-run the step-5 check · ${a.firstName || (a.role === 'coborrower' ? 'co-borrower' : 'primary')}` : 'Re-run the step-5 check'}
            confirmLabel="Re-run the check" testid="guided-module-step5-recheck"
            onConfirm={async (reason) => { await api.post(`/borrowers/${a.userId}/step5-recheck`, { reason }); await changed(); }} />
        ))}
        <GuidedCheckHolds borrowerId={borrowerId} token={token} kinds={['step5_check']} />
      </div>
    );
  }
  if (part === 'recheck') {
    return (
      <Card testid="guided-module-recheck" title="The step-5 check">
        <GuidedCheckHolds borrowerId={borrowerId} token={token} kinds={['step5_check']} />
        <Columns view={view}>{(a) => (
          <div>
            <ReasonAction label="Re-run the step-5 check" confirmLabel="Re-run the check" testid="guided-module-step5-recheck"
              onConfirm={async (reason) => { await api.post(`/borrowers/${a.userId}/step5-recheck`, { reason }); await changed(); }} />
          </div>
        )}</Columns>
      </Card>
    );
  }
  if (part === 'dispute-income' || part === 'dispute-assets') {
    const k = part === 'dispute-income' ? 'income' : 'assets';
    const rows = view.applicants.filter((a) => {
      const d = (a.facts && a.facts.confirmations && a.facts.confirmations.disputes) || {};
      return k === 'income' ? d.incomeDisputed && !d.incomeDisputeResolved : d.assetsDisputed && !d.assetsDisputeResolved;
    });
    if (!rows.length) return null;
    return (
      <div data-testid={`guided-module-step6-dispute-${k}`} style={{ background: '#ffffff', border: '1px solid #e6e6e6', borderTop: '5px solid #FFDD86', borderRadius: 12, padding: '20px 24px', display: 'grid', gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#111827' }}>{k === 'income' ? 'The borrower disputes this figure' : 'The borrower disputes these figures'}</h2>
        {rows.map((a) => (
          <div key={a.userId} style={{ display: 'grid', gap: 6 }}>
            {view.applicants.length > 1 && <div style={{ fontSize: 13, fontWeight: 700, color: B.ink }}>{nameOf(a)}</div>}
            <ReasonAction label="Resolve the dispute" testid={`guided-module-step6-resolve-${k}`}
              onConfirm={async (reason) => { await axios.post(`${API}/api/readycheck/resolve-${k}-dispute/${a.userId}`, { resolution: reason }, { headers }); await changed(); }} />
          </div>
        ))}
      </div>
    );
  }
  if (part === 'debts') {
    return (
      <Card testid="guided-module-debts" title="Debts the borrower confirmed">
        <Columns view={view}>{(a) => {
          const c = (a.facts && a.facts.confirmations) || {};
          return <Row label="Debts (monthly)" testid="guided-module-debts-monthly">{money(c.debt)}</Row>;
        }}</Columns>
      </Card>
    );
  }

  if (part === 'step56') {
    return (
      <Card testid="guided-module-step56" title="Steps 5–6 · documents check and the borrower's confirmation">
        <GuidedCheckHolds borrowerId={borrowerId} token={token} kinds={['step5_check']} />
        <Columns view={view}>{(a) => {
          const c = (a.facts && a.facts.confirmations) || {};
          const d = c.disputes || {};
          const o = override[a.userId] || {};
          const set = (k, v) => setOverride((m) => ({ ...m, [a.userId]: { ...(m[a.userId] || {}), [k]: v } }));
          return (<>
            <div>
              <ReasonAction label="Re-run the step-5 check" confirmLabel="Re-run the check" testid="guided-module-step5-recheck"
                onConfirm={async (reason) => { await api.post(`/borrowers/${a.userId}/step5-recheck`, { reason }); await changed(); }} />
            </div>
            {['income', 'assets'].map((k) => {
              const open = k === 'income' ? d.incomeDisputed && !d.incomeDisputeResolved : d.assetsDisputed && !d.assetsDisputeResolved;
              if (!open) return null;
              return (
                <div key={k} data-testid={`guided-module-step6-dispute-${k}`} style={{ background: B.redLight, border: `1px solid ${B.red}`, borderRadius: 8, padding: 10, display: 'grid', gap: 6 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: B.ink }}>{k === 'income' ? 'Income' : 'Savings'} dispute open</div>
                  <ReasonAction label="Resolve the dispute" testid={`guided-module-step6-resolve-${k}`}
                    onConfirm={async (reason) => { await axios.post(`${API}/api/readycheck/resolve-${k}-dispute/${a.userId}`, { resolution: reason }, { headers }); await changed(); }} />
                </div>
              );
            })}
            <Row label="Income (monthly)" testid="guided-module-step6-income">{money(c.income)}</Row>
            <Row label="Debts (monthly)">{money(c.debt)}</Row>
            <Row label="Savings">{money(c.assets)}</Row>
            <Row label="Confirmed">{[c.incomeConfirmedAt ? `income ${stamp(c.incomeConfirmedAt)}` : null, c.assetsConfirmedAt ? `savings ${stamp(c.assetsConfirmedAt)}` : null].filter(Boolean).join(' · ') || 'not yet'}</Row>
            {c.override && <Row label="Override on file" testid="guided-module-step6-override-on-file">
              {[c.override.income != null ? `income ${money(c.override.income)}` : null, c.override.assets != null ? `savings ${money(c.override.assets)}` : null].filter(Boolean).join(', ')}
              {c.override.at ? ` · ${stamp(c.override.at)}` : ''}{c.override.by ? ` · ${c.override.by}` : ''}{c.override.notes ? ` · “${c.override.notes}”` : ''}</Row>}
            <div>
              <ReasonAction label="Override and reopen step 6" confirmLabel="Override and reopen step 6" testid="guided-module-step6-override"
                extra={(
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
                    <label style={{ fontSize: 12, color: B.ink }}>Income per month
                      <input data-testid="guided-module-step6-override-income" value={o.income || ''} onChange={(e) => set('income', e.target.value)}
                        style={{ display: 'block', width: '100%', marginTop: 3, fontSize: 13, padding: 7, borderRadius: 6, border: `1px solid ${B.slate}`, boxSizing: 'border-box' }} /></label>
                    <label style={{ fontSize: 12, color: B.ink }}>Savings
                      <input data-testid="guided-module-step6-override-assets" value={o.assets || ''} onChange={(e) => set('assets', e.target.value)}
                        style={{ display: 'block', width: '100%', marginTop: 3, fontSize: 13, padding: 7, borderRadius: 6, border: `1px solid ${B.slate}`, boxSizing: 'border-box' }} /></label>
                    <div style={{ fontSize: 12, color: B.navy, gridColumn: '1 / -1' }}>The borrower confirms the new figure on step 6; a decided file re-decides then.</div>
                  </div>
                )}
                onConfirm={async (reason) => { await api.post(`/borrowers/${a.userId}/step6-override`, { income: o.income || null, assets: o.assets || null, reason }); setOverride((m) => ({ ...m, [a.userId]: {} })); await changed(); }} />
            </div>
          </>);
        }}</Columns>
      </Card>
    );
  }

  if (part === 'letters') {
    const closed = ['accepted', 'resolved', 'dismissed', 'rejected'];
    const rows = (loxConditions || []).filter((c) => !closed.includes(String(c.status || '').toLowerCase()) && !c.dismissed && !c.resolved);
    if (!rows.length) return null;
    return (
      <Card testid="guided-module-letters" title={`Dismiss a letter (${rows.length} open)`}>
        <div style={{ fontSize: 12, color: B.slate }}>Accept, ask for a new letter and compensating factors are in the letters list below.</div>
        {rows.map((c) => (
          <div key={c.id} data-testid="guided-module-letter" style={{ borderTop: `1px solid ${B.line}`, paddingTop: 8, display: 'grid', gap: 6 }}>
            <div style={{ fontSize: 13, color: B.ink }}>{String(c.canonical_category || c.category || '').replace(/_/g, ' ')} — {c.description || c.title} · letter #{c.id}</div>
            <div><ReasonAction label="Dismiss" confirmLabel="Dismiss the letter" testid="guided-module-letter-dismiss"
              onConfirm={async (reason) => { await api.patch(`/borrowers/${view.ownerId}/credit-conditions/${c.id}`, { action: 'dismiss', admin_note: reason }); await changed(); }} /></div>
          </div>
        ))}
      </Card>
    );
  }

  if (part === 'consents') {
    return (
      <Card testid="guided-module-consents" title="Consents and authorizations">
        <Columns view={view}>{(a) => (<>
          {(a.facts.consents || []).length === 0 && <div style={{ fontSize: 13, color: B.slate }}>None recorded.</div>}
          {(a.facts.consents || []).map((x, i) => (
            <div key={i} data-testid="guided-module-consent" style={{ fontSize: 13, borderTop: `1px solid ${B.line}`, padding: '6px 0', wordBreak: 'break-word' }}>
              <b>{consentLabel(x.kind)}</b>{cleanVersion(x.version) ? ` · ${cleanVersion(x.version)}` : ''} · {stamp(x.at)}{x.ip ? ` · ${x.ip}` : ''}{x.actingAdmin ? ` · by staff ${x.actingAdmin}` : ''}
              {x.userAgent && <div style={{ color: B.slate, fontSize: 12 }}>{x.userAgent}</div>}
            </div>
          ))}
          {a.facts.signature ? (
            <div data-testid="guided-module-signature" style={{ borderTop: `1px solid ${B.line}`, paddingTop: 6 }}>
              <Row label="Program agreement">signed {stamp(a.facts.signature.signedAt)}{a.facts.signature.agreementVersion ? ` · version ${a.facts.signature.agreementVersion}` : ''}</Row>
              <Row label="Signed from">{a.facts.signature.ip || '—'}</Row>
              <Row label="Device">{a.facts.signature.userAgent || '—'}</Row>
              {a.facts.signature.chosenPrice != null && <Row label="Price signed at">{money(a.facts.signature.chosenPrice)}</Row>}
            </div>
          ) : <div style={{ fontSize: 13, color: B.slate }}>Program agreement not signed.</div>}
          {/* 🔑 URLA Section 8: the status only, never the answers (2026-10-27). */}
          <div data-testid="guided-module-demographics" style={{ borderTop: `1px solid ${B.line}`, paddingTop: 6 }}>
            <Row label="Demographic information (URLA Section 8)">{a.facts.demographicsStatus || 'Not answered yet'}</Row>
          </div>
        </>)}</Columns>
      </Card>
    );
  }

  // part === 'decision'
  const s8 = view.step8 || {};
  const dec = s8.decision;
  return (
    <div data-testid="guided-module-decision-step" style={{ display: 'grid', gap: 14 }}>
      <GuidedCheckHolds borrowerId={borrowerId} token={token} kinds={['step7_pull', 'step8_decision']} />
      {/* 🔑 THE FILE'S CLOSURE, READ-ONLY (cleanup, Jesse 2026-10-29 — backend utils/guidedReviewFacts.decisionFacts): a
          classic-era file closed by staff may have no step-8 decision row; its closure and reasons are shown here. */}
      {s8.closure && (
        <Card testid="guided-module-closure" title="File closed">
          <Row label="Status">{String(s8.closure.status || '—').replace(/_/g, ' ')}{s8.closure.statusUpdatedAt ? ` · ${stamp(s8.closure.statusUpdatedAt)}` : ''}</Row>
          {s8.closure.adversed && <Row label="Adverse action">{s8.closure.adversedAt ? stamp(s8.closure.adversedAt) : 'recorded'}{s8.closure.creditBased ? ' · credit-based' : ''}{s8.closure.noticeSentAt ? ` · notice sent ${stamp(s8.closure.noticeSentAt)}` : ''}</Row>}
          {s8.closure.archived && <Row label="Archived">{s8.closure.archivedAt ? stamp(s8.closure.archivedAt) : 'yes'}</Row>}
          {(s8.closure.reasons || []).length > 0 && <Row label="Reasons">{s8.closure.reasons.join('; ')}</Row>}
        </Card>
      )}
      {/* 🔑 THE CLASSIC APPLICATION, READ-ONLY (Jesse, 2026-10-29 — backend utils/classicFreshStart): a classic-era borrower
          who started fresh answers into a new application; the classic answers are unchanged and shown here. */}
      {s8.classicApplication && (
        <Card testid="guided-module-classic-application" title={`Classic application (before the fresh start on ${day(s8.classicApplication.freshStartAt)})`}>
          <details>
            <summary style={{ cursor: 'pointer', fontSize: 13, color: B.ink }}>{(s8.classicApplication.answers || []).length} answers · last saved {stamp(s8.classicApplication.updatedAt)}</summary>
            {(s8.classicApplication.answers || []).map((a) => <Row key={a.key} label={a.key.replace(/_/g, ' ')}>{a.value}</Row>)}
          </details>
        </Card>
      )}
      <Card testid="guided-module-decision" title="The decision">
        {!dec && <div style={{ fontSize: 13, color: B.slate }}>No decision yet.</div>}
        {dec && (<>
          <Row label="Outcome">{dec.outcome}{dec.decidedAt ? ` · ${stamp(dec.decidedAt)}` : ''}</Row>
          <Row label="Approved maximum">{money(dec.approvedMax)}</Row>
          <Row label="Requested (step 2)">{dec.requestedPurchasePrice ? money(dec.requestedPurchasePrice) : 'none recorded'}</Row>
          <Row label="Rate · payment">{dec.rate != null ? formatRate(dec.rate) : '—'} · {money(dec.monthlyPayment)}/mo</Row>
          {dec.reasons && dec.reasons.length > 0 && <Row label="Reasons">{dec.reasons.join('; ')}</Row>}
          <div style={{ fontSize: 12, color: B.slate }}>Re-pricing goes through the decision path: the borrower signs again if the terms change, and gets a counteroffer notice if they're below the step-2 price.</div>
          <div><ReasonAction label="Re-price this decision" confirmLabel="Re-price" testid="guided-module-reprice"
            extra={<PricingInputs value={repriceIn} onChange={setRepriceIn} testid="guided-module-reprice-inputs" />}
            onConfirm={async (reason) => { await api.post(`/borrowers/${view.ownerId}/reprice-decision`, { reason, inputs: repriceIn }); setRepriceIn({}); await changed(); }} /></div>
        </>)}
      </Card>
      {s8.review && (
        <Card testid="guided-module-task" tone={s8.review.status === 'open' ? 'orange' : null} title={`Review task #${s8.review.id} · ${s8.review.status}`}>
          <div style={{ fontSize: 13, color: B.ink }}>{reasonWords(s8.review.routeReason)}{s8.review.dueAt ? ` · due ${day(s8.review.dueAt)}` : ''}</div>
          <div style={{ fontSize: 13, color: B.ink }}>Proposed: {(s8.review.proposed || []).map((r) => r.description || r.type).join('; ') || '—'}</div>
          {s8.review.status === 'open' ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <ReasonAction label="Uphold the decline" confirmLabel="Uphold and send the notices" testid="guided-module-task-uphold"
                onConfirm={async (reason) => { await api.post(`/atlas-reviews/${s8.review.id}/confirm`, { reason }); await changed(); }} />
              <ReasonAction label="Overturn and approve" confirmLabel="Overturn and decide now" testid="guided-module-task-overturn" kind="primary"
                extra={(
                  <div style={{ display: 'grid', gap: 8 }}>
                    {[['computed', 'Approve at the computed terms'], ['repriced', 'Approve at re-priced terms']].map(([m, label]) => (
                      <label key={m} style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center', color: B.ink }}>
                        <input type="radio" name={`overturn-${s8.review.id}`} data-testid={`guided-module-task-overturn-${m}`} checked={overturn.mode === m}
                          onChange={() => setOverturn((o) => ({ ...o, mode: m }))} />{label}
                      </label>
                    ))}
                    {overturn.mode === 'repriced' && <PricingInputs value={overturn.inputs} onChange={(v) => setOverturn((o) => ({ ...o, inputs: v }))} testid="guided-module-task-overturn-inputs" />}
                    <div style={{ fontSize: 12, color: B.navy }}>Decides now through the decision path: the borrower sees the new decision on step 8 at once (a counteroffer notice if below their step-2 price).</div>
                  </div>
                )}
                onConfirm={async (reason) => { await api.post(`/atlas-reviews/${s8.review.id}/overturn`, { reason, mode: overturn.mode, inputs: overturn.inputs }); setOverturn({ mode: 'computed', inputs: {} }); await changed(); }} />
            </div>
          ) : <div style={{ fontSize: 13, color: B.slate }}>{s8.review.reviewedBy ? `${s8.review.status} by ${s8.review.reviewedBy}` : ''}{s8.review.reviewedAt ? ` · ${stamp(s8.review.reviewedAt)}` : ''}</div>}
        </Card>
      )}
      {(s8.notices || []).length > 0 && (
        <Card testid="guided-module-notices" title="Notices">
          {s8.notices.map((x) => {
            const who = view.applicants.find((a) => a.userId === x.userId);
            return (
              <div key={x.id} data-testid="guided-module-notice" style={{ fontSize: 13, borderTop: `1px solid ${B.line}`, padding: '6px 0' }}>
                {x.kind === 'counteroffer' ? 'Counteroffer + adverse action' : 'Adverse action'} · {who ? nameOf(who) : `#${x.userId}`} · {stamp(x.sentAt)}
                {x.toEmail ? ` · to ${x.toEmail}` : ''}{x.delivery ? ` · ${x.delivery}` : ''}{x.score != null ? ` · score disclosed ${x.score}` : ''}
                {x.reasons && x.reasons.length > 0 && <div style={{ color: B.slate, fontSize: 12 }}>{x.reasons.join('; ')}</div>}
              </div>
            );
          })}
        </Card>
      )}
      <Label>Signatures, consents: see the Consents step</Label>
    </div>
  );
}
